<template>
  <Motion
    class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 animate-fade-in"
    :initial="{ opacity: 0, y: 16, scale: 0.98 }"
    :animate="{ opacity: 1, y: 0, scale: 1 }"
    :exit="{ opacity: 0, y: 8, scale: 0.98 }"
    :transition="{ type: 'spring', stiffness: 420, damping: 32, mass: 0.9 }"
  >
    <!-- Header -->
    <div class="bg-slate-800/50 border-b border-slate-700/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-white">Role Management</h1>
            <p class="text-slate-400 mt-1">Manage roles, permissions, and user access</p>
          </div>
          <div class="flex gap-3">
            <button
              @click="showCreateRoleModal = true"
              class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                ></path>
              </svg>
              Create Role
            </button>
            <button
              @click="activeTab = 'users'"
              :class="[
                'px-4 py-2 rounded-lg transition-colors',
                activeTab === 'users'
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-600 text-slate-300 hover:bg-slate-700',
              ]"
            >
              Manage Users
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Tabs -->
      <div class="mb-6">
        <div class="border-b border-slate-700">
          <nav class="-mb-px flex space-x-8">
            <button
              @click="activeTab = 'roles'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'roles'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300',
              ]"
            >
              Roles & Permissions
            </button>
            <button
              @click="activeTab = 'users'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300',
              ]"
            >
              User Roles
            </button>
          </nav>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span class="ml-3 text-slate-400">Loading...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
        <div class="flex items-center">
          <svg
            class="w-5 h-5 text-red-400 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <div>
            <h3 class="text-red-400 font-medium">Error</h3>
            <p class="text-red-300 text-sm mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Roles Tab -->
      <div v-else-if="activeTab === 'roles'">
        <!-- Roles Table -->
        <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden mb-6">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Permissions
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Users
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="role in roles" :key="role.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-white">{{ role.name }}</div>
                      <div class="text-sm text-slate-400">{{ role.description }}</div>
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="permission in role.permissions"
                        :key="permission.id"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900/20 text-blue-300"
                      >
                        {{ permission.name }}
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {{ getUsersWithRole(role.id).length }} users
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center gap-2">
                      <button
                        @click="editRole(role)"
                        class="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        @click="deleteRole(role)"
                        class="text-red-400 hover:text-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Users Tab -->
      <div v-else-if="activeTab === 'users'">
        <!-- Users with Roles Table -->
        <div class="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Roles
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr
                  v-for="user in usersWithRoles"
                  :key="user.id || user.user_id"
                  class="hover:bg-slate-700/30"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div
                        class="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center"
                      >
                        <span class="text-sm font-bold text-white">
                          {{ getUserInitials(user) }}
                        </span>
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-white">
                          {{ (user.name || user.user_metadata?.name) || user.email }}
                        </div>
                        <div class="text-sm text-slate-400">{{ user.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="role in user.roles"
                        :key="role.id"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900/20 text-green-300"
                      >
                        {{ role.name }}
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center gap-3">
                      <span v-if="!user.id && user.user_id" class="px-2 py-0.5 text-xs rounded bg-slate-700 text-slate-300">
                        IdP
                      </span>
                      <button
                        @click="manageUserRoles(user)"
                        :disabled="!user.id && user.user_id && (user.roles||[]).length===0"
                        class="text-blue-400 hover:text-blue-300 disabled:opacity-50 transition-colors"
                      >
                        Manage Roles
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit Role Modal -->
    <div
      v-if="showCreateRoleModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div class="bg-slate-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h3 class="text-lg font-medium text-white mb-4">
          {{ editingRole ? 'Edit Role' : 'Create Role' }}
        </h3>

        <form @submit.prevent="editingRole ? saveEditedRole() : createRole()">
          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-300 mb-2">Role Name</label>
            <input
              v-model="newRole.name"
              type="text"
              required
              :disabled="!!editingRole"
              class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="e.g., manager"
            />
            <p v-if="editingRole" class="text-xs text-slate-500 mt-1">
              Role name cannot be changed after creation.
            </p>
          </div>

          <div class="mb-6">
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              v-model="newRole.description"
              class="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Role description..."
              rows="3"
            ></textarea>
          </div>

          <div class="flex justify-end gap-3">
            <button
              type="button"
              @click="closeRoleModal"
              class="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="createLoading"
              class="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {{ createLoading ? 'Saving...' : editingRole ? 'Save Changes' : 'Create Role' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Manage User Roles Modal -->
    <div
      v-if="showManageRolesModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div class="bg-slate-800 rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 class="text-lg font-medium text-white mb-4">Manage Roles</h3>
        <p class="text-slate-300 mb-4">
          {{ ((selectedUser?.name || selectedUser?.user_metadata?.name) || selectedUser?.email) || 'User' }}
        </p>
        <div class="space-y-2 max-h-72 overflow-y-auto pr-1">
          <label
            v-for="role in roles"
            :key="role.id"
            class="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-700/40 cursor-pointer"
          >
            <input
              type="checkbox"
              class="h-4 w-4"
              :checked="selectedRoleIds.has(role.id)"
              @change="toggleRole(role.id, ($event.target as HTMLInputElement).checked)"
            />
            <span class="text-slate-100 text-sm">{{ role.name }}</span>
            <span class="text-slate-400 text-xs">{{ role.description }}</span>
          </label>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            @click="closeManageRoles()"
            class="px-4 py-2 text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="savingRoles"
            @click="applyUserRoles()"
            class="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            {{ savingRoles ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </Motion>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { Motion } from 'motion-v'
import { roleManagementService, type Role, type UserRole } from '@/lib/roleManagementService'
import { createLogger } from '@/lib/logger'

const log = createLogger('RoleManagement')

// State
const activeTab = ref<'roles' | 'users'>('roles')
const roles = ref<Role[]>([])
const usersWithRoles = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showCreateRoleModal = ref(false)
const createLoading = ref(false)
const editingRole = ref<Role | null>(null)

// Manage roles modal state
const showManageRolesModal = ref(false)
const selectedUser = ref<any | null>(null)
const selectedRoleIds = ref<Set<string>>(new Set())
const savingRoles = ref(false)

// New role form
const newRole = ref({
  name: '',
  description: '',
})

// Load data on mount
onMounted(() => {
  loadRoles()
  loadUsersWithRoles()
})

// Computed
const getUsersWithRole = (roleId: string) => {
  return usersWithRoles.value.filter((user) => user.roles.some((role: any) => role.id === roleId))
}

// Load roles
async function loadRoles() {
  loading.value = true
  error.value = null

  try {
    roles.value = await roleManagementService.getRoles()
  } catch (err) {
    log.error('Failed to load roles:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load roles'
  } finally {
    loading.value = false
  }
}

// Load users with roles
async function loadUsersWithRoles() {
  try {
    usersWithRoles.value = await roleManagementService.getUsersWithRoles()
  } catch (err) {
    log.error('Failed to load users with roles:', err)
  }
}

// Get user initials
function getUserInitials(user: any): string {
  if (user.name) {
    return user.name
      .split(' ')
      .map((part: string) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return user.email.split('@')[0].slice(0, 2).toUpperCase()
}

// Create role
async function createRole() {
  createLoading.value = true

  try {
    await roleManagementService.createRole(newRole.value)
    newRole.value = { name: '', description: '' }
    showCreateRoleModal.value = false
    await loadRoles()
  } catch (err) {
    log.error('Failed to create role:', err)
    error.value = err instanceof Error ? err.message : 'Failed to create role'
  } finally {
    createLoading.value = false
  }
}

/**
 * Open the role modal in edit mode, pre-filling with the selected role's data.
 */
function editRole(role: Role) {
  editingRole.value = role
  newRole.value = { name: role.name, description: role.description || '' }
  showCreateRoleModal.value = true
}

/**
 * Persist the edited role's description back to the database.
 */
async function saveEditedRole() {
  if (!editingRole.value) return
  createLoading.value = true
  try {
    await roleManagementService.updateRole(editingRole.value.id, {
      description: newRole.value.description,
    })
    closeRoleModal()
    await loadRoles()
  } catch (err) {
    log.error('Failed to update role:', err)
    error.value = err instanceof Error ? err.message : 'Failed to update role'
  } finally {
    createLoading.value = false
  }
}

/**
 * Close the create/edit role modal and reset form state.
 */
function closeRoleModal() {
  showCreateRoleModal.value = false
  editingRole.value = null
  newRole.value = { name: '', description: '' }
}

// Delete role
async function deleteRole(role: Role) {
  if (
    !confirm(
      `Are you sure you want to delete the "${role.name}" role? This will remove it from all users.`,
    )
  ) {
    return
  }

  try {
    await roleManagementService.deleteRole(role.id)
    await loadRoles()
  } catch (err) {
    log.error('Failed to delete role:', err)
    error.value = err instanceof Error ? err.message : 'Failed to delete role'
  }
}

// Manage user roles
function manageUserRoles(user: any) {
  selectedUser.value = user
  const current = new Set<string>((user.roles || []).map((r: any) => r.id))
  selectedRoleIds.value = current
  showManageRolesModal.value = true
}

function toggleRole(roleId: string, checked: boolean) {
  if (checked) selectedRoleIds.value.add(roleId)
  else selectedRoleIds.value.delete(roleId)
}

function closeManageRoles() {
  showManageRolesModal.value = false
  selectedUser.value = null
  selectedRoleIds.value = new Set()
}

async function applyUserRoles() {
  if (!selectedUser.value) return
  savingRoles.value = true
  try {
    const userId = selectedUser.value.id || selectedUser.value.user_id
    const existing: string[] = (selectedUser.value.roles || []).map((r: any) => r.id)
    const desired = Array.from(selectedRoleIds.value)

    const toAdd = desired.filter((id) => !existing.includes(id))
    const toRemove = existing.filter((id) => !desired.includes(id))

    for (const roleId of toAdd) {
      await roleManagementService.assignRoleToUser(userId, roleId)
    }
    for (const roleId of toRemove) {
      await roleManagementService.removeRoleFromUser(userId, roleId)
    }

    await loadUsersWithRoles()
    closeManageRoles()
  } catch (err) {
    log.error('Failed to apply roles:', err)
    alert('Failed to save roles')
  } finally {
    savingRoles.value = false
  }
}
</script>
